import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, Check, Crown, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useMembership } from "@/contexts/membership-context";
import { usePricingTiers } from "@/hooks/use-pricing-tiers";
import type { PricingTier } from "@/types/pricing";
import { queryKeys } from "@/lib/query-keys";

const TIER_ICONS = {
  basic: Sparkles,
  pro: Crown,
  ultimate: Building2,
} as const;

function formatCurrency(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function CheckoutConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { upgradeToTier, isLoading } = useMembership();

  const tierId = searchParams.get("tier");
  const billingPeriodParam = searchParams.get("billingPeriod");
  const billingPeriod: "monthly" | "yearly" =
    billingPeriodParam === "yearly" ? "yearly" : "monthly";

  const { data: allTiers = [], isLoading: isLoadingTiers } = useQuery<PricingTier[]>({
    queryKey: queryKeys.subscriptions.productTiers(),
    queryFn: usePricingTiers,
  });

  const tier = useMemo(
    () => allTiers.find((candidate) => candidate.id === tierId),
    [allTiers, tierId],
  );

  useEffect(() => {
    if (!tierId || !billingPeriodParam) {
      toast.error(t("pricing.invalidSelection", "Plan inválido"));
      navigate("/pricing");
    }
  }, [tierId, billingPeriodParam, navigate, t]);

  useEffect(() => {
    if (!isLoadingTiers && allTiers.length > 0 && tierId && !tier) {
      toast.error(t("pricing.tierNotFound", "No encontramos el plan elegido"));
      navigate("/pricing");
    }
  }, [isLoadingTiers, allTiers, tier, tierId, navigate, t]);

  const handleConfirm = async () => {
    if (!tier) {
      return;
    }
    const productId = billingPeriod === "yearly" ? tier.yearly?.id : tier.monthly?.id;
    if (!productId) {
      toast.error(t("pricing.productUnavailable", "Este plan no está disponible"));
      return;
    }
    await upgradeToTier(productId, billingPeriod);
  };

  if (isLoadingTiers || !tier) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const Icon = TIER_ICONS[tier.id as keyof typeof TIER_ICONS] ?? Sparkles;
  const price = tier.price[billingPeriod];
  const currency = tier[billingPeriod]?.prices[0]?.priceCurrency?.toUpperCase() ?? "USD";

  return (
    <div className="container mx-auto flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <CardTitle className="font-bold text-2xl">
              {t("pricing.confirmYourPlan", "Confirmá tu plan")}
            </CardTitle>
            <CardDescription>
              {t("pricing.confirmDescription", "Revisá los detalles antes de ir al pago")}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="rounded-xl border border-border bg-muted/20 p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="font-semibold text-lg">{tier.name}</div>
                {tier.description && (
                  <div className="text-muted-foreground text-sm">{tier.description}</div>
                )}
              </div>
              <Badge variant="outline">
                {billingPeriod === "yearly"
                  ? t("billing.yearly", "Anual")
                  : t("billing.monthly", "Mensual")}
              </Badge>
            </div>
            <Separator className="my-3" />
            <div className="flex items-baseline gap-2">
              <span className="font-bold text-3xl tracking-tight">
                {price === 0 ? "Free" : formatCurrency(price, currency)}
              </span>
              {price > 0 && (
                <span className="text-muted-foreground text-sm">
                  /{billingPeriod === "yearly" ? "año" : "mes"}
                </span>
              )}
            </div>
          </div>

          {tier.features && tier.features.length > 0 && (
            <ul className="space-y-2.5">
              {tier.features.slice(0, 6).map((feature) => (
                <li className="flex items-start gap-3 text-muted-foreground text-sm" key={feature}>
                  <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Check className="h-2.5 w-2.5 text-primary" />
                  </div>
                  <span>{t(`pricing.features.${feature}`, feature)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>

        <CardFooter className="flex flex-col gap-2">
          <Button className="w-full" disabled={isLoading} onClick={handleConfirm} size="lg">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.processing", "Procesando...")}
              </>
            ) : (
              <>
                {t("pricing.confirmAndPay", "Confirmar e ir al pago")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
          <Button
            className="w-full"
            disabled={isLoading}
            onClick={() => navigate("/pricing")}
            variant="ghost"
          >
            {t("pricing.backToPlans", "Volver a los planes")}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
